import { useQueryClient } from "@tanstack/react-query";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import TimetableDayOffsetDropdown from "components/dropdowns/reactselect/TimetableDayOffsetDropdown";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import React, { useCallback } from "react";
import {
  getCreateTimetableEntryInstruction,
  getInitialTimetableEntryFormState,
  maxTimetableNotesLength,
  type TimetableEntryFormState
} from "shared/utils/fieldsHelper/timetableEntryFields";
import { invalidateTimetableCache } from "utilities/api/timetableQuery";
import { TimetableTimeInput } from "timetable/TimetableTimeInput";
import { TimetableTypeInput } from "timetable/TimetableTypeInput";
import { useOpenSidePanel } from "../SidePanel";

interface Props {
  showId: number
}

const NewTimetableSidePanel = (props: Props) => {
  const [formState, setFormState] = React.useState<TimetableEntryFormState>(getInitialTimetableEntryFormState());
  const queryClient = useQueryClient();

  const onChange = (newData: Partial<TimetableEntryFormState>) => {
    setFormState(prevState => ({ ...prevState, ...newData }));
  };

  return <SidePanelNewSectionTemplate
    afterCreate="closeSidebar"
    onSave={() => { invalidateTimetableCache(queryClient); }}
    title={RESX.Timetable.AddItem}
    id="new-timetable-side-panel"
    trigger={undefined}
    submit={() => window.CoreApi.Timetable.CreateTimetableEntry(getCreateTimetableEntryInstruction(props.showId, formState))}
    sections={[{
      key: "timetableEntry",
      type: "regular",
      hideTitle: true,
      title: RESX.Timetable.TimetableItem,
      fields: [{
        key: "startTime",
        label: RESX.DateTime.Starts,
        render: () => <TimetableTimeInput
          value={formState.startTime}
          onChange={startTime => onChange({ startTime })}
          validatorKey="startTime"
          isRequired={formState.endTime != null}
        />
      }, {
        key: "endTime",
        label: RESX.DateTime.Ends,
        render: () => <TimetableTimeInput
          value={formState.endTime}
          onChange={endTime => onChange({ endTime })}
          validatorKey="endTime"
          isRequired={false}
        />
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        render: () => <TimetableDayOffsetDropdown
          value={formState.dayOffset}
          onChange={dayOffset => onChange({ dayOffset })}
        />
      }, {
        key: "type",
        label: RESX.GeneralLabels.Type,
        render: () => <TimetableTypeInput
          value={{ type: formState.type, customText: formState.customText }}
          validatorKey="type"
          onChange={value => onChange({ type: value.type, customText: value.customText })}
        />
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        render: () => <OptionalTextAreaInput
          maxLength={maxTimetableNotesLength}
          rows={3}
          value={formState.notes}
          onChange={notes => onChange({ notes })}
        />
      }]
    }]}
  />;
};

export const useOpenNewTimetableSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewTimetableSidePanel = useCallback((showId: number) => {
    openSidePanel(
      <NewTimetableSidePanel showId={showId} />,
      "new-timetable-side-panel"
    );
  }, [openSidePanel]);

  return openNewTimetableSidePanel;
};
